import { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Facebook, Send, Mail } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { CONTACT, CONTACT_SECTION } from "@/data/content";

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const handleChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Uzupełnij wszystkie pola formularza.");
      return;
    }
    setSending(true);
    // Tylko frontend — symulacja wysyłki. Podłącz backend / e-mail we własnym zakresie.
    setTimeout(() => {
      setSending(false);
      setForm({ name: "", email: "", message: "" });
      toast.success("Dziękujemy! Odezwiemy się najszybciej jak to możliwe.");
    }, 800);
  };

  return (
    <section
      id="kontakt"
      data-testid="contact-section"
      className="bg-[#EAE4D9] py-20 md:py-32"
    >
      <div className="mx-auto max-w-7xl px-5 md:px-10">
        <div className="mx-auto max-w-2xl text-center">
          <span className="text-sm font-semibold uppercase tracking-[0.2em] text-[#4A7055]">
            {CONTACT_SECTION.eyebrow}
          </span>
          <h2 className="mt-4 font-display text-3xl font-semibold leading-tight text-[#2D3728] text-balance md:text-4xl lg:text-5xl">
            {CONTACT_SECTION.title}
          </h2>
          <p className="mt-4 text-base leading-relaxed text-[#4A5D43] md:text-lg">
            {CONTACT_SECTION.description}
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 gap-8 lg:grid-cols-5">
          <motion.form
            data-testid="contact-form"
            onSubmit={handleSubmit}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="rounded-3xl bg-white p-7 shadow-sm md:p-9 lg:col-span-3"
          >
            <div className="space-y-5">
              <div>
                <Label htmlFor="name" className="text-[#2D3728]">
                  Imię i nazwisko
                </Label>
                <Input
                  id="name"
                  name="name"
                  data-testid="contact-name-input"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="Jan Kowalski"
                  className="mt-2 border-[#D8CFBC] bg-[#F9F7F3] focus-visible:ring-[#4A7055]"
                />
              </div>
              <div>
                <Label htmlFor="email" className="text-[#2D3728]">
                  Adres e-mail
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  data-testid="contact-email-input"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="jan@przyklad.pl"
                  className="mt-2 border-[#D8CFBC] bg-[#F9F7F3] focus-visible:ring-[#4A7055]"
                />
              </div>
              <div>
                <Label htmlFor="message" className="text-[#2D3728]">
                  Wiadomość
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  data-testid="contact-message-input"
                  value={form.message}
                  onChange={handleChange}
                  rows={5}
                  placeholder="Interesuje mnie termin, liczba osób..."
                  className="mt-2 border-[#D8CFBC] bg-[#F9F7F3] focus-visible:ring-[#4A7055]"
                />
              </div>
              <button
                type="submit"
                data-testid="contact-submit-button"
                disabled={sending}
                className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#4A7055] px-8 py-4 text-sm font-semibold text-[#F9F7F3] transition-all hover:bg-[#3d5c47] hover:-translate-y-0.5 disabled:opacity-60"
              >
                <Send className="h-4 w-4" />
                {sending ? "Wysyłanie..." : "Wyślij zapytanie"}
              </button>
            </div>
          </motion.form>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="flex flex-col gap-4 lg:col-span-2"
          >
            <a
              href={CONTACT.phoneHref}
              data-testid="contact-call-button"
              className="group flex flex-1 flex-col justify-between rounded-3xl bg-[#4A7055] p-7 text-white transition-all hover:-translate-y-1 hover:bg-[#3d5c47]"
            >
              <Phone className="h-8 w-8 text-[#E8D9B5]" />
              <div className="mt-8">
                <p className="text-sm uppercase tracking-wide text-white/70">
                  Zadzwoń do nas
                </p>
                <p className="mt-1 font-display text-2xl font-semibold">
                  {CONTACT.phone}
                </p>
              </div>
            </a>

            <a
              href={CONTACT.facebook}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-facebook-button"
              className="group flex flex-1 flex-col justify-between rounded-3xl bg-[#9C5E4B] p-7 text-white transition-all hover:-translate-y-1 hover:bg-[#88503f]"
            >
              <Facebook className="h-8 w-8 text-[#F1E3CE]" />
              <div className="mt-8">
                <p className="text-sm uppercase tracking-wide text-white/70">
                  Napisz do nas
                </p>
                <p className="mt-1 font-display text-2xl font-semibold">
                  Facebook
                </p>
              </div>
            </a>

            <a
              href={`mailto:${CONTACT.email}`}
              data-testid="contact-email-link"
              className="flex items-center gap-3 rounded-3xl border border-[#D8CFBC] bg-white p-5 text-[#2D3728] transition-colors hover:border-[#4A7055]"
            >
              <Mail className="h-5 w-5 text-[#4A7055]" />
              <span className="text-sm font-medium">{CONTACT.email}</span>
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
